import type { ProjectTemplate, ProjectTemplateSummary } from '@servicetim/shared';
import {
  adminPath,
  useApiContext,
  useDeleteTemplate,
  useTemplate,
  useTemplates,
} from '@servicetim/ui/api';
import {
  Button,
  buttonVariants,
  EmptyState,
  LoadingState,
  ProblemBanner,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Link } from '@tanstack/react-router';
import { Download, Info, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { downloadJson, slugify } from '../projects/lib/download';
import { notifyError } from '../projects/lib/notify';
import { TemplateCard } from './template-card';
import { summarizeTemplate } from './template-summary';

/** Seeded + saved templates as cards with details, export and delete. */
export function TemplateCenter() {
  const templates = useTemplates(100);
  if (templates.isPending) return <LoadingState rows={3} />;
  if (templates.error) {
    return <ProblemBanner error={templates.error} onRetry={() => void templates.refetch()} />;
  }
  if (templates.items.length === 0) {
    return (
      <EmptyState
        title="No templates"
        description="Import a template document or export one from an existing project."
      />
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.items.map((template) => (
          <TemplateCenterCard key={template.id} template={template} />
        ))}
      </div>
      {templates.hasMore ? (
        <Button variant="outline" onClick={templates.loadMore} disabled={templates.isLoadingMore}>
          Load more
        </Button>
      ) : null}
    </div>
  );
}

function TemplateCenterCard({ template }: { template: ProjectTemplateSummary }) {
  const [showDetails, setShowDetails] = useState(false);
  const detail = useTemplate(showDetails ? template.id : undefined);
  const { request } = useApiContext();
  const remove = useDeleteTemplate();
  const confirm = useConfirm();
  const [exporting, setExporting] = useState(false);

  const exportJson = async () => {
    setExporting(true);
    try {
      const full =
        detail.data ??
        (await request<ProjectTemplate>('GET', adminPath(`/project-templates/${template.id}`)));
      downloadJson(`${slugify(template.name)}.template.json`, full.document);
    } catch (error) {
      notifyError(error, 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Delete template "${template.name}"?`,
      description: 'Projects created from it are not affected.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: () => remove.mutateAsync(template.id),
    });
    if (ok) toast.success('Template deleted');
  };

  return (
    <TemplateCard
      template={template}
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDetails((v) => !v)}
            aria-expanded={showDetails}
          >
            <Info />
            {showDetails ? 'Hide details' : 'Details'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void exportJson()}
            disabled={exporting}
          >
            <Download />
            Export JSON
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300"
            onClick={() => void onDelete()}
            disabled={template.isSeeded}
            title={template.isSeeded ? 'Seeded templates cannot be deleted' : undefined}
          >
            <Trash2 />
            Delete
          </Button>
          <Link
            to="/projects/new"
            className={buttonVariants({ variant: 'link', size: 'sm' })}
            aria-label={`Create a project from ${template.name}`}
          >
            Use template
          </Link>
        </>
      }
    >
      {showDetails ? (
        detail.isPending ? (
          <LoadingState rows={2} />
        ) : detail.error ? (
          <ProblemBanner error={detail.error} size="sm" />
        ) : detail.data ? (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            {summarizeTemplate(detail.data.document).map((c) => (
              <div key={c.label} className="contents">
                <dt className="text-fg-muted">{c.label}</dt>
                <dd className="font-medium tabular-nums">{c.count}</dd>
              </div>
            ))}
            <dt className="text-fg-muted">version</dt>
            <dd className="font-medium tabular-nums">{detail.data.version}</dd>
            {detail.data.sourceProjectId ? (
              <>
                <dt className="text-fg-muted">exported from</dt>
                <dd>
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: detail.data.sourceProjectId }}
                    className="text-accent hover:underline"
                  >
                    project
                  </Link>
                </dd>
              </>
            ) : null}
          </dl>
        ) : null
      ) : null}
    </TemplateCard>
  );
}
